import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { Alert, Form, Pagination } from 'react-bootstrap'
import Icon from 'components/ui/icon'

import Page from 'components/ui/page'
import PageHeader from 'components/ui/pageHeader'
import EmptyState from 'components/ui/emptyState'
import UsersTable from './components/usersTable'
import * as api from './api.client'
import { AdminUser } from './types'

const PAGE_SIZE = 20

const AdminUsers: FunctionComponent = () => {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loaded, setLoaded] = useState(false)
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [nameFilter, setNameFilter] = useState('')
  const [appliedNameFilter, setAppliedNameFilter] = useState('')
  const [errorMessage, setErrorMessage] = useState({ show: false, message: '' })

  const loadUsers = useCallback(async (pageNumber: number, name: string) => {
    try {
      const result = await api.getUsers(pageNumber, PAGE_SIZE, name)
      setUsers(result.content)
      setPage(result.page)
      setTotalPages(result.totalPages)
      setAppliedNameFilter(name)
      setErrorMessage({ show: false, message: '' })
    } catch (error) {
      setErrorMessage({
        show: true,
        message: 'Erro ao buscar os usuários.',
      })
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    loadUsers(0, '')
  }, [loadUsers])

  const searchByName = (event: React.FormEvent) => {
    event.preventDefault()
    loadUsers(0, nameFilter.trim())
  }

  return (
    <Page fill>
      <PageHeader
        title="Usuários"
        subtitle="Pessoas com acesso à plataforma Clic-repasse."
        actions={
          <Form
            onSubmit={searchByName}
            role="search"
            className="field-wrap"
            style={{ width: '18rem', maxWidth: '100%' }}
          >
            <Icon name="search" size={16} />
            <Form.Control
              type="search"
              placeholder="Buscar por nome"
              aria-label="Buscar usuários por nome"
              value={nameFilter}
              onChange={(event) => setNameFilter(event.currentTarget.value)}
            />
          </Form>
        }
      />

      {errorMessage.show && (
        <Alert variant="danger">{errorMessage.message}</Alert>
      )}

      {loaded && users.length === 0 && !errorMessage.show ? (
        <EmptyState
          icon={<Icon name="users" size={20} />}
          title="Nenhum usuário encontrado"
        />
      ) : (
        <UsersTable users={users} />
      )}

      {totalPages > 1 && (
        <Pagination className="mt-3 justify-content-center">
          <Pagination.Prev
            disabled={page === 0}
            onClick={() => loadUsers(page - 1, appliedNameFilter)}
          />
          {Array.from({ length: totalPages }, (_, index) => (
            <Pagination.Item
              key={index}
              active={index === page}
              onClick={() => loadUsers(index, appliedNameFilter)}
            >
              {index + 1}
            </Pagination.Item>
          ))}
          <Pagination.Next
            disabled={page >= totalPages - 1}
            onClick={() => loadUsers(page + 1, appliedNameFilter)}
          />
        </Pagination>
      )}
    </Page>
  )
}

export default AdminUsers
